import React, { memo, useContext } from 'react';
import dynamic from 'next/dynamic';
import { SearchModes, type Transport } from '@/domainLogic/index.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { BADGES_LAYOUT, ResetPosition } from '@/app/components/search/components/NoResults/BadgesContainer/types.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { getSearchParamsClient } from '@/app/components/search/common/getSearchParams.ts';
import Subtitle from '@/app/components/search/components/Title/Subtitle';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import MyPriceModal from '@/app/components/search/components/Modal/Modals/MyPriceModal';
import { useSearchDefaultData } from '@/app/components/search/searchDefaultDataProvider.tsx';
import OvernightFlightsModal from '@/app/components/search/components/Modal/Modals/OvernightFlightsModal';
import TaxesIncludedModal from '@/app/components/search/components/Modal/Modals/TaxesIncludedModal';
import { MainTitleStyled, TitleStyled } from './styled';
import getTitleProps from './utils';
import type { DynamicFiltersResponse } from '../../utils/api/dynamicFiltersResponseTypes';

const BadgesContainer = dynamic(() => import('../NoResults/BadgesContainer'), { ssr: false });
const LoadLine = dynamic(() => import('@/app/components/Loadline.tsx'), { ssr: false });
const CustomCheckinCheckoutBanner = dynamic(() => import('../Banners/CustomCheckinCheckoutBanner'), { ssr: false });

export type TitleProps = {
  isIndex?: boolean;
  isWaiting: boolean;
  transport?: Transport;
  dynamicFiltersResponse?: DynamicFiltersResponse;
};

const Title = memo(
  ({ isIndex, isWaiting, transport, dynamicFiltersResponse }: TitleProps) => {
    const {
      searchParams: searchParamsServer,
      searchInfoData,
      searchInfoDetailData,
      localization,
      commonData,
    } = usePageWrapperData();
    const { defaultValues } = useSearchDefaultData();
    const { productsData } = useSearchPageData();
    const { isMobile, isDesktop, isTablet } = useCommonClient();
    const { authResponse } = commonData;
    const { searchMode, isLiveSearch } = searchInfoData;
    const { accData } = productsData;
    const { hasPreselectedFlight } = useSearchDealsPageData();
    const { t } = useTranslation();
    const { listView, triggerTaxesIncludedTooltip } = useContext(SearchPageContext);
    const searchParams = getSearchParamsClient(searchParamsServer);
    const dealsCount = productsData?.productCounters?.productsCount || 0;
    const { defaultDates: searchDates } = defaultValues;
    const customCheckinCheckoutEnabled = localization?.configValuesApp?.customCheckinCheckoutEnabled;
    const debugEnabled = localization?.configValuesApp?.debugEnabled;
    const isDP = searchMode === SearchModes.DP;
    const isTrain = searchMode === SearchModes.TH;
    const isHO = searchMode === SearchModes.HO;
    const hasResults = dealsCount > 0;
    const hideTitle = isIndex ? false : isDP || isTrain || isMobile;
    const hasCustomCheckinCheckoutFilter = !!searchParams?.prefilter_checkin_checkout && isDP && isLiveSearch;
    const showCustomCheckinCheckout = customCheckinCheckoutEnabled && isDP && isLiveSearch;
    const showCustomCheckinCheckoutControl = showCustomCheckinCheckout && debugEnabled;
    const showCustomCheckinCheckoutBanner = hasCustomCheckinCheckoutFilter && hasResults;

    const { text, subTitle, loadingTitle } = getTitleProps({
      searchParams,
      searchMode,
      searchInfoDetailData,
      searchDates,
      dealsCount,
      localization,
      isMobile,
      hasPreselectedFlight,
      showCustomCheckinCheckout: showCustomCheckinCheckoutControl,
      accData,
      t,
    });
    const hideSubTitleText = isIndex ? false : !subTitle && !isWaiting;
    const isListViewWaiting = isWaiting && listView;
    const addMargin = (searchMode === SearchModes.HO && !isIndex) || (isListViewWaiting && !hasPreselectedFlight);
    const showLoadLine = isWaiting && isDesktop && listView && isHO;
    const showBadgeContainer = (isTablet || isMobile) && !isIndex && (dealsCount > 0 || isWaiting);
    const showTitle = !(hideTitle && hideSubTitleText);
    const myPriceModalEnabled = localization?.configValuesApp?.myPriceModalEnabled;
    const showMyPriceModal = myPriceModalEnabled && isHO && isLiveSearch && !authResponse.loggedIn;
    const overnightAvailable =
      localization?.configValuesApp?.overnightFlightsEnabled && transport?.overnightAvailable && isDP;
    const taxesIncludedV3Available = localization?.configValuesApp?.taxesIncludedPopup;

    return (
      <ErrorBoundary id="Title">
        {showTitle && (
          <TitleStyled
            isIndex={isIndex}
            isWaiting={isWaiting}
            listView={listView}
            hasPreselectedFlight={hasPreselectedFlight}
            searchMode={searchMode}
          >
            {!hideTitle && (
              <MainTitleStyled
                hasPreselectedFlight={hasPreselectedFlight}
                isWaiting={isWaiting && hasPreselectedFlight}
              >
                {text}
              </MainTitleStyled>
            )}
            {showLoadLine && <LoadLine listView={listView} addMargin={addMargin} isDP={isDP} style={{ bottom: -8 }} />}
            <Subtitle
              isWaiting={isWaiting}
              loadingTitle={loadingTitle}
              subTitle={subTitle}
              isIndex={isIndex}
              transport={transport}
              showCustomCheckinCheckout={showCustomCheckinCheckoutControl}
              hideSubTitleText={hideSubTitleText}
            />
            {showMyPriceModal && <MyPriceModal t={t} loggedIn={authResponse?.loggedIn} />}
            {showBadgeContainer && (
              <BadgesContainer
                resetPosition={ResetPosition.Right}
                style={{ marginTop: 16 }}
                dynamicFiltersResponse={dynamicFiltersResponse}
                layout={BADGES_LAYOUT.MULTIPLE_ROWS}
              />
            )}
            {showCustomCheckinCheckoutBanner && <CustomCheckinCheckoutBanner />}
            {overnightAvailable && <OvernightFlightsModal localization={localization} />}
            {taxesIncludedV3Available && (
              <TaxesIncludedModal localization={localization} onModalClose={triggerTaxesIncludedTooltip} />
            )}
          </TitleStyled>
        )}
      </ErrorBoundary>
    );
  },
  (prevProps: TitleProps, nextProps: TitleProps) => {
    if (prevProps.isWaiting !== nextProps.isWaiting) return false;
    if (prevProps.transport !== nextProps.transport) return false;
    return true;
  }
);

Title.displayName = 'Title';

export default Title;
